import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useApolloClient } from '@apollo/client/react';
import {
  GroupType,
  useProjectDeleteSelectedMutation,
  ProjectAnnotationStateQuery,
  ProjectAnnotationStateQueryVariables,
} from '../../types/graphqlTypes';
import { PROJECT_ANNOTATION_STATE } from '../../graphql/project';
import { roomFolderIdForRoom, roomIdFromAutoFolderId } from '../../utils/roomFolders';
import { scheduleDeleteMutation, trackPendingStateMutation } from './annotationMutationCoalescer';
import { clientId } from '../pusher';
import { getOpeningRoomIdsProperty, getRoomGroupLinkProperty, ROOM_GROUP_LINK_NONE } from '@pointorama/potree-shared';

/**
 * Optimistically remove annotations and groups from Apollo cache so the UI
 * updates immediately. Mirrors the server's cascades:
 * - openings whose last linked room is being deleted are removed too;
 *   openings that keep at least one link get `opening_room_ids` updated;
 * - a deleted group pulls its nested groups along (floor → room folders);
 * - a deleted room measure pulls its auto tag folder;
 * - directly-deleted ROOM folders DISSOLVE: their children re-parent to the
 *   parent floor instead of being deleted.
 */
function removeSelectedFromCache(
  apolloClient: ReturnType<typeof useApolloClient>,
  projectId: string,
  annotationIds: string[],
  groupIds: string[],
): { deletedIds: string[]; movedIds: { identifier: string; groupIdentifier: string | null }[] } {
  try {
    const cached = apolloClient.readQuery<ProjectAnnotationStateQuery, ProjectAnnotationStateQueryVariables>({
      query: PROJECT_ANNOTATION_STATE,
      variables: { projectId },
    });
    if (!cached?.projectById) return { deletedIds: [...annotationIds, ...groupIds], movedIds: [] };

    const state = cached.projectById.state;
    const allAnnotations = state.annotations || [];
    const allGroups = state.groups || [];
    const groupsByIdentifier = new Map(allGroups.map((g) => [g.identifier, g]));

    // Transitively include nested groups of deleted groups.
    const groupIdSet = new Set(groupIds);
    let expanded = true;
    while (expanded) {
      expanded = false;
      for (const g of allGroups) {
        if (g.parentIdentifier && groupIdSet.has(g.parentIdentifier) && !groupIdSet.has(g.identifier)) {
          groupIdSet.add(g.identifier);
          expanded = true;
        }
      }
    }

    // Deleted room measures pull their auto tag folders along.
    const explicitAnnotationIdSet = new Set(annotationIds);
    for (const ann of allAnnotations) {
      if (ann.type !== 'ROOM' || !explicitAnnotationIdSet.has(ann.identifier)) continue;
      const folder = groupsByIdentifier.get(roomFolderIdForRoom(ann.identifier));
      if (folder) groupIdSet.add(folder.identifier);
    }

    // Directly-deleted ROOM folders dissolve (children survive, re-parented).
    const dissolvedGroupIds = new Set(
      [...groupIdSet].filter((id) => {
        const g = groupsByIdentifier.get(id);
        return g?.type === GroupType.Room && !(g.parentIdentifier && groupIdSet.has(g.parentIdentifier));
      }),
    );

    // Collect child annotation ids that belong to deleted (non-dissolved) groups
    const groupChildIds = allAnnotations
      .filter((a) => a.groupIdentifier && groupIdSet.has(a.groupIdentifier) && !dissolvedGroupIds.has(a.groupIdentifier))
      .map((a) => a.identifier);

    const baseDeleted = new Set([...annotationIds, ...groupChildIds]);

    // Room→opening cascade: find rooms in the deletion set, then walk every
    // remaining opening and apply the same filter the server performs.
    const deletedRoomIds = new Set(
      allAnnotations.filter((a) => a.type === 'ROOM' && baseDeleted.has(a.identifier)).map((a) => a.identifier),
    );

    const cascadedOpeningIds: string[] = [];
    if (deletedRoomIds.size > 0) {
      for (const ann of allAnnotations) {
        if (ann.type !== 'OPENING') continue;
        if (baseDeleted.has(ann.identifier)) continue;
        const prop = getOpeningRoomIdsProperty(ann.identifier);
        const roomIds = prop.value;
        if (!roomIds.length) continue;
        const remaining = roomIds.filter((id) => !deletedRoomIds.has(id));
        if (remaining.length === roomIds.length) continue;
        if (remaining.length === 0) {
          cascadedOpeningIds.push(ann.identifier);
        } else {
          prop.value = remaining;
        }
      }
    }

    const deletedAnnotations = new Set([...baseDeleted, ...cascadedOpeningIds]);
    const deletedAll = new Set([...deletedAnnotations, ...groupIdSet]);

    // Survivors of dissolved folders: their optimistic groupIdentifier needs
    // field-level clobber protection (`moved`) while the delete is in flight.
    const movedIds: { identifier: string; groupIdentifier: string | null }[] = [];

    apolloClient.writeQuery<ProjectAnnotationStateQuery, ProjectAnnotationStateQueryVariables>({
      query: PROJECT_ANNOTATION_STATE,
      variables: { projectId },
      data: {
        ...cached,
        projectById: {
          ...cached.projectById,
          state: {
            ...state,
            annotations: allAnnotations
              .filter((a) => !deletedAnnotations.has(a.identifier))
              .map((a) => {
                if (!a.groupIdentifier || !dissolvedGroupIds.has(a.groupIdentifier)) return a;
                const parentId = groupsByIdentifier.get(a.groupIdentifier)?.parentIdentifier ?? null;
                movedIds.push({ identifier: a.identifier, groupIdentifier: parentId });
                return { ...a, groupIdentifier: parentId };
              }),
            groups: allGroups.filter((g) => !groupIdSet.has(g.identifier)),
            orderedIdentifiers: (state.orderedIdentifiers || []).filter((o) => !deletedAll.has(o.identifier)),
          },
        },
      },
    });
    return { deletedIds: [...deletedAll], movedIds };
  } catch {
    // Cache update failed — mutation response will reconcile
    return { deletedIds: [...annotationIds, ...groupIds], movedIds: [] };
  }
}

export function useDeleteAnnotation() {
  const { projectId } = useParams<{ projectId: string }>();
  const [deleteSelectedMutation] = useProjectDeleteSelectedMutation();
  const apolloClient = useApolloClient();

  const deleteAnnotation = useCallback(
    (identifier: string) => {
      if (!projectId) return;
      removeSelectedFromCache(apolloClient, projectId, [identifier], []);
      // Defer the mutation through the coalescer so a rapid create/delete
      // pair (e.g. from spammed Ctrl+Z / Ctrl+Shift+Z across an area point
      // removal) cancels both server round-trips. The optimistic cache
      // write above keeps the UI in sync regardless.
      scheduleDeleteMutation(identifier, () => deleteSelectedMutation({ variables: { projectId, annotationIdentifiers: [identifier], groupIdentifiers: [], clientId } }));
    },
    [projectId, deleteSelectedMutation, apolloClient],
  );

  const deleteAnnotationsMulti = useCallback(
    (identifiers: string[]) => {
      if (!projectId) return;
      const { deletedIds, movedIds } = removeSelectedFromCache(apolloClient, projectId, identifiers, []);
      trackPendingStateMutation({ deleted: deletedIds, moved: movedIds }, deleteSelectedMutation({ variables: { projectId, annotationIdentifiers: identifiers, groupIdentifiers: [], clientId } }));
    },
    [projectId, deleteSelectedMutation, apolloClient],
  );

  /** Delete a mixed selection of annotations and groups in a single mutation. */
  const deleteSelected = useCallback(
    (items: Array<{ identifier: string; type: 'group' | 'annotation' | string }>) => {
      if (!projectId) return;
      const groupIds: string[] = [];
      const annotationIds: string[] = [];
      for (const item of items) {
        if (item.type === 'group') {
          groupIds.push(item.identifier);
        } else {
          annotationIds.push(item.identifier);
        }
      }

      // Deleted auto room-tag folders need their tombstone written BEFORE the
      // delete lands, or reconciliation recreates them from the still-existing
      // room measure. Owned here so every delete path (panel menu, keyboard
      // shortcut, future callers) is covered.
      for (const folderId of groupIds) {
        const roomId = roomIdFromAutoFolderId(folderId);
        if (!roomId) continue;
        try {
          getRoomGroupLinkProperty(roomId).value = ROOM_GROUP_LINK_NONE;
        } catch {
          // property service not ready — reconciliation may recreate once
        }
      }

      const { deletedIds, movedIds } = removeSelectedFromCache(apolloClient, projectId, annotationIds, groupIds);
      trackPendingStateMutation(
        { deleted: deletedIds, moved: movedIds },
        deleteSelectedMutation({
          variables: { projectId, annotationIdentifiers: annotationIds, groupIdentifiers: groupIds, clientId },
        }),
      );
    },
    [projectId, deleteSelectedMutation, apolloClient],
  );

  /**
   * Delete a WALL SKETCH with its floor-wide cascade: every room folder and
   * every sketch-derived annotation (SKETCH_OBJECT / OPENING_SKETCH) on the
   * sketch's floor goes with it — the sketch is the source those hang off.
   * ROOM measures are deliberately KEPT: they're measured data, not
   * sketch-derived. Executed as ONE mutation via `deleteSelected` (which
   * also writes the room-folder tombstones, so reconciliation doesn't
   * recreate the folders from the surviving room measures).
   */
  const deleteWallSketchWithCascade = useCallback(
    (identifier: string) => {
      if (!projectId) return;
      let items: Array<{ identifier: string; type: 'group' | 'annotation' }> = [{ identifier, type: 'annotation' }];
      try {
        const cached = apolloClient.readQuery<ProjectAnnotationStateQuery, ProjectAnnotationStateQueryVariables>({
          query: PROJECT_ANNOTATION_STATE,
          variables: { projectId },
        });
        const state = cached?.projectById?.state;
        const sketch = state?.annotations?.find((a) => a.identifier === identifier);
        const floorId = sketch?.groupIdentifier ?? null;
        if (state && floorId) {
          const groupsById = new Map((state.groups || []).map((g) => [g.identifier, g]));
          const isOnFloor = (groupId: string | null | undefined): boolean => {
            let current = groupId ?? null;
            for (let guard = 0; current && guard < 100; guard++) {
              if (current === floorId) return true;
              current = groupsById.get(current)?.parentIdentifier ?? null;
            }
            return false;
          };
          for (const ann of state.annotations || []) {
            if (ann.identifier === identifier) continue;
            if ((ann.type === 'SKETCH_OBJECT' || ann.type === 'OPENING_SKETCH') && isOnFloor(ann.groupIdentifier)) {
              items.push({ identifier: ann.identifier, type: 'annotation' });
            }
          }
          for (const group of state.groups || []) {
            if (group.type === GroupType.Room && isOnFloor(group.identifier)) {
              items.push({ identifier: group.identifier, type: 'group' });
            }
          }
        }
      } catch {
        // Cache unavailable — fall back to deleting just the sketch; the
        // server/reconciliation will surface any leftovers.
        items = [{ identifier, type: 'annotation' }];
      }
      deleteSelected(items);
    },
    [projectId, apolloClient, deleteSelected],
  );

  return { deleteAnnotation, deleteAnnotationsMulti, deleteSelected, deleteWallSketchWithCascade };
}
